import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@/hooks/use-translation'
import * as api from '../api/brand-voice-api'
import type { UpdateBrandVoiceDto } from '../types/brand-voice.types'

export function useGetBrandVoices() {
  return useQuery({
    queryKey: ['brand-voices'],
    queryFn: api.getBrandVoices,
  })
}

export function useGetBrandVoice(id: string) {
  return useQuery({
    queryKey: ['brand-voices', id],
    queryFn: () => api.getBrandVoiceById(id),
    enabled: Boolean(id),
  })
}

export function useCreateBrandVoice() {
  const queryClient = useQueryClient()
  const t = useTranslation()
  return useMutation({
    mutationFn: api.createBrandVoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-voices'] })
      toast.success(t('brandVoice.toast.createSuccess'))
    },
    onError: (error: Error) => {
      toast.error(t('brandVoice.toast.createError', { message: error.message }))
    },
  })
}

export function useUpdateBrandVoice() {
  const queryClient = useQueryClient()
  const t = useTranslation()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBrandVoiceDto }) =>
      api.updateBrandVoice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-voices'] })
      toast.success(t('brandVoice.toast.updateSuccess'))
    },
    onError: (error: Error) => {
      toast.error(t('brandVoice.toast.updateError', { message: error.message }))
    },
  })
}

export function useDeleteBrandVoice() {
  const queryClient = useQueryClient()
  const t = useTranslation()
  return useMutation({
    mutationFn: api.deleteBrandVoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-voices'] })
      toast.success(t('brandVoice.toast.deleteSuccess'))
    },
    onError: (error: Error) => {
      toast.error(t('brandVoice.toast.deleteError', { message: error.message }))
    },
  })
}

export function useDuplicateBrandVoice() {
  const queryClient = useQueryClient()
  const t = useTranslation()
  return useMutation({
    mutationFn: api.duplicateBrandVoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-voices'] })
      toast.success(t('brandVoice.toast.duplicateSuccess'))
    },
    onError: (error: Error) => {
      toast.error(t('brandVoice.toast.duplicateError', { message: error.message }))
    },
  })
}
